import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {SiteData} from "types/Site";
import SiteForm from "pages/Site/Form";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";

function MutateSite() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const {data: site, isLoading} = useQuery(
    ['sites', {id}],
    () => ngage.sites.get(Number(id)),
    {enabled: !!id && auth.can('sites:read')}
  )

  const store = useMutation((data: SiteData) => ngage.sites.store(data))
  const update = useMutation((data: SiteData) => ngage.sites.update(Number(id), data))

  // Handle form submission
  async function handleSubmit(values: SiteData, {setSubmitting}: FormikHelpers<SiteData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('sites.all')

    setSubmitting(false)
    navigate('/sites')
  }

  // Handle store mutation
  async function handleStore(values: SiteData) {
    try {
      await store.mutateAsync(values)
      notify("success", `${termFor('Site')} Created`, `${values.name} has been created`)
    } catch (error) {
      notify("error", `${termFor('Site')} Not Created`, `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: SiteData) {
    try {
      await update.mutateAsync(values)
      notify("success", `${termFor('Site')} Updated`, `${values.name ?? site?.name} has been updated`)
      await invalidate(['sites', {id}])
    } catch (error) {
      notify("error", `${termFor('Site')} Not Updated`, `${values.name ?? site?.name} could not be updated`)
      throw error
    }
  }

  // Prevent unauthorised mutation
  if (auth.cannot('sites:write') && auth.cannot('site-hours:write')) {
    return <NotAuthorised/>
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'Operations': '/',
    [termFor('Sites')]: '/sites',
    [`New ${termFor('Site')}`]: '/sites/new'
  }

  const editBreadcrumbs = {
    'Operations': '/',
    [termFor('Sites')]: '/sites',
    [`Edit ${termFor('Site')}`]: `/sites/${id}/edit`
  }

  return (
    <Page
      title={`${id ? 'Edit' : 'New'} ${termFor('Site')}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <SiteForm site={site} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateSite;